import { useState } from 'react';
import { parseCapabilityURN, groupCapabilitiesByDomain, buildSummaryLine } from './parseCapabilityURN.js';
import { safeStr } from './applicationDetailShared.js';
import './applications.css';

// SensitivityBadge renders a chip for high and critical sensitivity levels
// only. Low and normal are intentionally omitted — absence of a badge means
// "nothing to worry about." (JOBE-0148.1 spec §Row layout, Decision 6.)
function SensitivityBadge({ sensitivity }) {
  if (!sensitivity || typeof sensitivity !== 'string' || sensitivity === 'low' || sensitivity === 'normal') return null;
  const cls = sensitivity === 'critical' ? 'badge crit' : 'badge high';
  return <span className={cls}>{sensitivity.toUpperCase()}</span>;
}

// CapabilityRow renders a single capability row with the new two-column layout:
//   Left: "{Domain} · {Action}" label, full URN (copyable), description
//   Right: sensitivity badge
// The `rowHover` state is managed by the parent group so the ⎘ glyph appears
// on row-level hover.
function CapabilityRow({ cap }) {
  const parsed = cap._parsed || parseCapabilityURN(cap.id);
  const [copyState, setCopyState] = useState(null); // null | 'copied'
  const [hovered, setHovered] = useState(false);

  const handleCopyURN = async () => {
    try {
      await navigator.clipboard.writeText(cap.id);
    } catch {
      // Clipboard write may fail in test/insecure contexts — fail silently.
    }
    setCopyState('copied');
    setTimeout(() => setCopyState(null), 800);
  };

  return (
    <div
      className="capRow"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="capRowLeft">
        <div className="capLabel">{safeStr(parsed.label)}</div>
        <div
          className={`capURN${hovered ? ' hovered' : ''}`}
          role="button"
          tabIndex={0}
          onClick={handleCopyURN}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCopyURN(); } }}
          title="Click to copy URN"
        >
          <span className="capURNText">
            {copyState === 'copied' ? 'copied' : (safeStr(cap.id) || '')}
          </span>
          {hovered && copyState !== 'copied' && <span className="capCopyGlyph">⎘</span>}
        </div>
        {safeStr(cap.description) && <div className="capDesc">{cap.description}</div>}
      </div>
      <div className="capRowRight">
        <SensitivityBadge sensitivity={cap.sensitivity} />
      </div>
    </div>
  );
}

// CapabilityDomainGroup renders a collapsible domain group heading + rows.
function CapabilityDomainGroup({ group, defaultExpanded }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  // group.label is the canonical title-cased domain display string set by
  // groupCapabilitiesByDomain (via domainLabel()). Read it directly — do not
  // re-derive from cap._parsed.label.
  const groupDisplayLabel = group.label;

  return (
    <div className="capGroup">
      <button
        type="button"
        className="cgh"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="cghLeft">
          <span className="cghChevron">{expanded ? '▾' : '▸'}</span>
          <b>{groupDisplayLabel}</b>
        </span>
        <span className="cghCount">{group.capabilities.length}</span>
      </button>
      {expanded && (
        <div className="capGroupRows">
          {group.capabilities.map((cap) => (
            <CapabilityRow key={cap.id} cap={cap} />
          ))}
        </div>
      )}
    </div>
  );
}

// CapabilitiesTab — redesigned per JOBE-0148.1.
// Domain-grouped, sensitivity-aware, copyable URN.
// Provider section header is SKIPPED for single-provider apps (every live
// app today). Multi-provider header is gated on JOBE-0149. (Decision 1.)
export function CapabilitiesTab({ app }) {
  const caps = app.capabilities || [];

  if (caps.length === 0) {
    return (
      <div style={{ padding: '48px 0', fontFamily: 'var(--ff-mono)', color: 'var(--ink-soft)', textAlign: 'center', fontStyle: 'italic' }}>
        No capabilities defined for this provider. Check the provider configuration or Provider CR.
      </div>
    );
  }

  const groups = groupCapabilitiesByDomain(caps);
  const summaryLine = buildSummaryLine(caps);

  // Collapse default: expanded when groups ≤ 6 AND caps ≤ 20. (Decision 3.)
  const defaultExpanded = groups.length <= 6 && caps.length <= 20;

  return (
    <div className="caps">
      {summaryLine && (
        <div className="capSummary hint">{summaryLine}</div>
      )}
      {groups.map((group) => (
        <CapabilityDomainGroup
          key={group.domain}
          group={group}
          defaultExpanded={defaultExpanded}
        />
      ))}
    </div>
  );
}
